import * as React from "react";
import { Link } from "react-router-dom";
import { formatDate, formatCurrency } from "app/utils/filters";
import { Button } from "@material-ui/core";
import {
  TicketEvent,
  TicketEventLocation,
  TicketOption,
} from "./ticketEventTypes";
import { renderColour, renderStatusBadge } from "./ticketEventColumns";
import TicketEventApproveButton from "./TicketEventApproveButton";
import TicketEventRequestChangesButton from "./TicketEventRequestChangesButton";

const merchantIcon = (ticketEvent: TicketEvent): string | undefined => {
  if (!ticketEvent.merchant || !ticketEvent.merchant.store_infos) {
    return undefined;
  }

  const storeInfoWithIcon = ticketEvent.merchant.store_infos.find(
    (storeInfo) => storeInfo.icon
  );

  return storeInfoWithIcon && storeInfoWithIcon.icon
    ? storeInfoWithIcon.icon
    : undefined;
};

export const renderDetailedLocation = (location?: TicketEventLocation) => {
  if (!location) {
    return "-";
  }

  if (typeof location === "string") {
    try {
      const parsedLocation = JSON.parse(location);
      if (parsedLocation && typeof parsedLocation === "object") {
        return renderDetailedLocation(parsedLocation);
      }
    } catch (error) {
      return location;
    }

    return location;
  }

  if (typeof location === "object") {
    const lines = [
      location.address,
      location.city,
      location.country_code || location.country,
    ].filter(Boolean);

    if (lines.length === 0) {
      return JSON.stringify(location);
    }

    return (
      <div style={{ lineHeight: 1.35, fontSize: "14px", color: "#555" }}>
        {lines.map((line, index) => (
          <div key={index}>{line}</div>
        ))}
      </div>
    );
  }

  return "-";
};

export const renderApprovalComments = (comments?: any) => {
  if (comments === null || comments === undefined) {
    return null;
  }

  if (Array.isArray(comments)) {
    const parsedComments = comments.filter(Boolean).map(String);

    if (parsedComments.length === 0) {
      return null;
    }

    return (
      <div
        style={{
          backgroundColor: "#F5F5F5",
          border: "1px solid #E3E3E3",
          padding: "8px 10px",
          borderRadius: "4px",
          display: "inline-block",
          maxWidth: "100%",
          fontSize: "14px",
          color: "#555",
        }}
      >
        {parsedComments.map((comment, index) => (
          <div
            key={index}
            style={{
              marginBottom: index === parsedComments.length - 1 ? "0" : "6px",
              lineHeight: 1.4,
            }}
          >
            {comment}
          </div>
        ))}
      </div>
    );
  }

  if (typeof comments === "string") {
    const trimmedComments = comments.trim();

    if (
      trimmedComments === "" ||
      trimmedComments === "[]" ||
      trimmedComments === "{}" ||
      trimmedComments.toLowerCase() === "null"
    ) {
      return null;
    }

    try {
      const parsedValue = JSON.parse(trimmedComments);

      if (Array.isArray(parsedValue)) {
        const parsedComments = parsedValue.filter(Boolean).map(String);

        if (parsedComments.length === 0) {
          return null;
        }

        return (
          <div
            style={{
              backgroundColor: "#F5F5F5",
              border: "1px solid #E3E3E3",
              padding: "8px 10px",
              borderRadius: "4px",
              display: "inline-block",
              maxWidth: "100%",
              fontSize: "14px",
              color: "#555",
            }}
          >
            {parsedComments.map((comment, index) => (
              <div
                key={index}
                style={{
                  marginBottom:
                    index === parsedComments.length - 1 ? "0" : "6px",
                  lineHeight: 1.4,
                }}
              >
                {comment}
              </div>
            ))}
          </div>
        );
      }
    } catch (error) {
      // treat as plain string below
    }

    return (
      <div
        style={{
          backgroundColor: "#F5F5F5",
          border: "1px solid #E3E3E3",
          padding: "8px 10px",
          borderRadius: "4px",
          display: "inline-block",
          maxWidth: "100%",
          fontSize: "14px",
          color: "#555",
        }}
      >
        {trimmedComments}
      </div>
    );
  }

  return null;
};

export const renderLogo = (logo?: string, name?: string) => {
  if (!logo) {
    return (
      <div style={{ color: "#888", fontSize: "14px" }}>
        No merchant logo available
      </div>
    );
  }

  return (
    <div
      style={{
        width: "120px",
        height: "120px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        backgroundColor: "#fafafa",
        border: "1px solid #d7d7d7",
        borderRadius: "4px",
        boxSizing: "border-box",
      }}
    >
      <img
        src={logo}
        alt={name || "Merchant logo"}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          objectFit: "contain",
          display: "block",
        }}
      />
    </div>
  );
};

export const renderSectionTitle = (title: string) => (
  <div
    style={{
      fontSize: "16px",
      fontWeight: 400,
      marginBottom: "10px",
      color: "#4a4a4a",
      borderBottom: "1px solid #d7d7d7",
      paddingBottom: "8px",
    }}
  >
    {title}
  </div>
);

type InfoTableRow = {
  label: string;
  value: React.ReactNode;
  labelWidth?: string;
};

export const TicketEventInfoTable = ({ rows }: { rows: InfoTableRow[] }) => (
  <table
    style={{
      width: "100%",
      fontSize: "14px",
    }}
    cellPadding={0}
    cellSpacing={0}
    className="BOTable"
  >
    <tbody>
      {rows.map((row, index) => (
        <tr key={index}>
          <td
            style={{
              width: row.labelWidth || "190px",
              fontWeight: 600,
              padding: "10px 14px",
              borderBottom:
                index === rows.length - 1 ? "none" : "1px solid #d7d7d7",
              verticalAlign: "top",
              color: "#555",
              fontSize: "14px",
            }}
          >
            {row.label}
          </td>
          <td
            style={{
              padding: "10px 14px",
              borderBottom:
                index === rows.length - 1 ? "none" : "1px solid #d7d7d7",
              verticalAlign: "top",
              color: "#555",
              fontSize: "14px",
            }}
          >
            {row.value || "-"}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const TicketEventSummaryHeader = ({
  ticketEvent,
}: {
  ticketEvent: TicketEvent;
}) => (
  <div
    style={{
      padding: "16px 20px 12px 20px",
      borderBottom: "1px solid #d7d7d7",
      backgroundColor: "#fff",
    }}
  >
    <h2
      style={{
        margin: "0 0 8px 0",
        fontSize: "20px",
        fontWeight: 600,
        color: "#4a4a4a",
      }}
    >
      {ticketEvent.name}
    </h2>

    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        alignItems: "center",
        color: "#666",
        fontSize: "14px",
      }}
    >
      <span>
        <strong>Status:</strong>{" "}
        {renderStatusBadge(ticketEvent.status, ticketEvent.end_date)}
      </span>
    </div>
  </div>
);

const buildFacebookPageUrl = (facebookPage?: string) => {
  if (!facebookPage) {
    return undefined;
  }

  return `https://www.facebook.com/${facebookPage}`;
};

const buildInstagramPageUrl = (instagramPage?: string) => {
  if (!instagramPage) {
    return undefined;
  }

  return `https://www.instagram.com/${instagramPage}`;
};

const renderExternalLink = (url?: string, label?: string) => {
  if (!url) {
    return "-";
  }

  return (
    <a
      className="blueLink"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {label || url}
    </a>
  );
};

export const TicketEventDetailsSection = ({
  ticketEvent,
}: {
  ticketEvent: TicketEvent;
}) => (
  <div style={{ marginBottom: "18px" }}>
    {renderSectionTitle("Details")}
    <div style={{ backgroundColor: "#fff", border: "1px solid #d7d7d7" }}>
      <TicketEventInfoTable
        rows={[
          {
            label: "Merchant",
            value:
              ticketEvent.merchant && ticketEvent.merchant.id ? (
                <Link
                  className="blueLink"
                  to={`/snapscan/ops/merchants/${ticketEvent.merchant.id}/payments`}
                >
                  {ticketEvent.merchant.name}
                </Link>
              ) : ticketEvent.merchant && ticketEvent.merchant.name ? (
                ticketEvent.merchant.name
              ) : (
                "-"
              ),
          },
          { label: "Code", value: ticketEvent.code },
          { label: "Description", value: ticketEvent.description },
          {
            label: "Facebook page",
            value: renderExternalLink(
              buildFacebookPageUrl(ticketEvent.facebook_page),
              ticketEvent.facebook_page
            ),
          },
          {
            label: "Instagram page",
            value: renderExternalLink(
              buildInstagramPageUrl(ticketEvent.instagram_page),
              ticketEvent.instagram_page
            ),
          },
          {
            label: "Ticket sales close date",
            value: ticketEvent.ticket_sales_close_date
              ? formatDate(ticketEvent.ticket_sales_close_date)
              : "-",
          },
          {
            label: "Start date",
            value: ticketEvent.start_date
              ? formatDate(ticketEvent.start_date)
              : "-",
          },
          {
            label: "End date",
            value: ticketEvent.end_date
              ? formatDate(ticketEvent.end_date)
              : "-",
          },
          {
            label: "Available tickets",
            value:
              ticketEvent.available_tickets !== undefined &&
              ticketEvent.available_tickets !== null
                ? ticketEvent.available_tickets
                : "-",
          },
          {
            label: "Sold tickets",
            value:
              ticketEvent.sold_tickets !== undefined &&
              ticketEvent.sold_tickets !== null
                ? ticketEvent.sold_tickets
                : "-",
          },
          {
            label: "External reference",
            value: ticketEvent.external_reference,
          },
        ]}
      />
    </div>
  </div>
);

export const TicketEventActionsSection = ({
  ticketEvent,
  showActions,
  reloadData,
  redirectPath,
}: {
  ticketEvent: TicketEvent;
  showActions: boolean;
  reloadData: () => void;
  redirectPath: string;
}) => {
  if (!showActions) {
    return (
      <div
        style={{
          marginBottom: "18px",
          padding: "12px 14px",
          border: "1px solid #d7d7d7",
          backgroundColor: "#fcfcfc",
          color: "#666",
          fontSize: "14px",
        }}
      >
        Only events in pending approval or changes requested can be actioned.
      </div>
    );
  }

  return (
    <div
      style={{
        marginBottom: "18px",
        display: "flex",
        gap: "10px",
        flexWrap: "wrap",
      }}
    >
      <TicketEventApproveButton
        id={ticketEvent.id}
        reloadData={reloadData}
        redirectPath={redirectPath}
      />
      <TicketEventRequestChangesButton
        id={ticketEvent.id}
        reloadData={reloadData}
        redirectPath={redirectPath}
      />
    </div>
  );
};

export const TicketEventTicketOptionsSection = ({
  ticketEvent,
}: {
  ticketEvent: TicketEvent;
}) => (
  <div
    style={{
      marginBottom: "18px",
      width: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    {renderSectionTitle("Ticket Options")}

    <div
      style={{
        backgroundColor: "#fff",
        border: "1px solid #d7d7d7",
        marginBottom: "12px",
      }}
    >
      <TicketEventInfoTable
        rows={[
          {
            label: "Series",
            value:
              ticketEvent.ticket_event_series &&
              ticketEvent.ticket_event_series.name
                ? ticketEvent.ticket_event_series.name
                : "-",
          },
        ]}
      />
    </div>

    <div style={{ marginBottom: "16px" }}>
      <div
        style={{
          fontWeight: 600,
          marginBottom: "8px",
          color: "#555",
          fontSize: "14px",
        }}
      >
        Merchant Custom Ticket Types
      </div>
      {!ticketEvent.active_custom_ticket_types ||
      ticketEvent.active_custom_ticket_types.length === 0 ? (
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #d7d7d7",
            padding: "10px 14px",
            color: "#888",
            fontSize: "14px",
          }}
        >
          No unverified custom ticket types for this merchant
        </div>
      ) : (
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #d7d7d7",
            padding: "8px 12px",
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          {ticketEvent.active_custom_ticket_types.map((customType) => (
            <span
              key={customType.id}
              style={{
                backgroundColor: "#eef2f7",
                border: "1px solid #cbd5e1",
                borderRadius: "4px",
                padding: "4px 8px",
                fontSize: "13px",
                color: "#334155",
                fontWeight: 500,
              }}
            >
              {customType.name}
            </span>
          ))}
        </div>
      )}
    </div>

    {!ticketEvent.ticket_options || ticketEvent.ticket_options.length === 0 ? (
      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #d7d7d7",
          padding: "12px 14px",
          color: "#888",
          fontSize: "14px",
        }}
      >
        No ticket options linked to this event
      </div>
    ) : (
      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #d7d7d7",
          overflowX: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            fontSize: "14px",
          }}
          cellPadding={0}
          cellSpacing={0}
          className="BOTable"
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: "10px 14px",
                  color: "#555",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                Ticket type
              </th>
              <th
                style={{
                  textAlign: "right",
                  padding: "10px 14px",
                  color: "#555",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                Price
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "10px 14px",
                  color: "#555",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                Multi-use
              </th>
            </tr>
          </thead>
          <tbody>
            {ticketEvent.ticket_options.map(
              (ticketOption: TicketOption, index: number) => (
                <tr key={ticketOption.id || index}>
                  <td
                    style={{
                      padding: "10px 14px",
                      color: "#555",
                      fontSize: "14px",
                      verticalAlign: "top",
                    }}
                  >
                    {ticketOption.ticket_type && ticketOption.ticket_type.name
                      ? ticketOption.ticket_type.name
                      : "-"}
                  </td>
                  <td
                    style={{
                      padding: "10px 14px",
                      textAlign: "right",
                      color: "#555",
                      fontSize: "14px",
                      verticalAlign: "top",
                    }}
                  >
                    {typeof ticketOption.price === "number"
                      ? `R${formatCurrency(ticketOption.price)}`
                      : "-"}
                  </td>
                  <td
                    style={{
                      padding: "10px 14px",
                      color: "#555",
                      fontSize: "14px",
                      verticalAlign: "top",
                    }}
                  >
                    {ticketOption.multi_use ? "Yes" : "No"}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export const TicketEventReviewSection = ({
  ticketEvent,
}: {
  ticketEvent: TicketEvent;
}) => {
  const approvalComments = renderApprovalComments(
    ticketEvent.approval_audit_trail
  );

  return (
    <div style={{ marginBottom: "18px" }}>
      {renderSectionTitle("Location & Approval Comments")}
      <div style={{ backgroundColor: "#fff", border: "1px solid #d7d7d7" }}>
        <TicketEventInfoTable
          rows={[
            {
              label: "Location",
              value: renderDetailedLocation(ticketEvent.location),
            },
            {
              label: "Approval comments",
              value: approvalComments || "",
            },
          ]}
        />
      </div>
    </div>
  );
};

export const TicketEventBrandingSection = ({
  ticketEvent,
}: {
  ticketEvent: TicketEvent;
}) => (
  <div
    style={{
      marginBottom: "18px",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      minHeight: "100%",
    }}
  >
    {renderSectionTitle("Event Branding")}
    <div
      style={{
        backgroundColor: "#fff",
        border: "1px solid #d7d7d7",
        padding: "12px",
        flex: 1,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: "16px" }}>
        <div
          style={{
            fontWeight: 600,
            marginBottom: "8px",
            color: "#555",
            fontSize: "14px",
          }}
        >
          Colour
        </div>
        {renderColour(ticketEvent.colour)}
      </div>

      <div>
        <div
          style={{
            fontWeight: 600,
            marginBottom: "8px",
            color: "#555",
            fontSize: "14px",
          }}
        >
          Merchant logo
        </div>
        {renderLogo(merchantIcon(ticketEvent), ticketEvent.merchant?.name)}
      </div>
    </div>
  </div>
);

export const BottomBackBar = ({
  backLabel,
  backPath,
  history,
}: {
  backLabel: string;
  backPath: string;
  history: any;
}) => (
  <div
    style={{
      borderTop: "1px solid #d7d7d7",
      padding: "12px 20px 16px 20px",
      display: "flex",
      justifyContent: "flex-start",
      backgroundColor: "#fff",
    }}
  >
    <Button
      variant="contained"
      style={{
        backgroundColor: "#4d5a86",
        color: "white",
        minWidth: "140px",
      }}
      onClick={() => history.push(backPath)}
    >
      {backLabel}
    </Button>
  </div>
);
